// Step 3, Site-by-site: one building at a time, written for Amazon. A verdict, the facts,
// where it is in the story, the current blockers from either side, the milestones and the
// programme. Tabs across the top hold the five buildings with the earliest committed COD.
import { Fragment, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";

import type { AmazonBuilding } from "../../api/hooks/useAmazonOverview";
import {
  asOnStepOne,
  type Blocker,
  blockerCards,
  blockersFor,
  cardState,
  forBuilding,
  OWNER_NAME,
  riskCauses,
  STATE_LABEL,
} from "./blockers";
import { useReviewComments } from "../../api/hooks/useReview";
import { UserContext } from "../../auth";
import { Chevron } from "../../components/Chevron";
import { ReviewProvider } from "../../components/ReviewProvider";
import type { StoryStep } from "./blueprint";
import { capitalised } from "./cells";
import { ChapterPanel } from "./ChapterPanel";
import {
  addDays,
  day,
  daysBetween,
  formatDate,
  formatNumeric,
  formatShort,
  today,
  uncountedText,
} from "./dates";
import { EditLayout, EditPane } from "./EditPane";
import { type Target, useEdit } from "./editContext";
import { Editable } from "./editing";
import { useFitBeside } from "./fitBeside";
import { HoverCard } from "./HoverCard";
import { Milestones } from "./Milestones";
import { NeededFromAmazon } from "./NeededFromAmazon";
import { OpenPoints } from "./OpenPoints";
import { PartyChip } from "./PartyChip";
import { RiskList } from "./RiskList";
import { RoofProcessLink, RoofProcessTitle } from "./RoofProcess";
import { blockerItems } from "./stepRecord";
import {
  buildingKey,
  cardKey,
  cardKeys,
  stepFactKeys,
  stepKey,
  isHidden,
  wordingOf,
} from "./overrides";
import { designInputs, owedOutside, pointNeeds } from "./needs";
import { DR3_PARTS, LANES, type PathStep, stepById } from "./pathSteps";
import {
  DEFAULT_BUFFER_DAYS,
  designLength,
  installLength,
  type Links,
  type Scheduled,
  lengthWhy,
  neededBy,
  neededByChain,
  plannedStartWhy,
  programmeLinks,
  projectCod,
  schedule,
  slipTone,
  soonest,
  uncountedFor,
} from "./schedule";
import { achievableSize } from "./achievable";
import { pairParts, ppaText } from "./sizes";
import styles from "./amazonOverview.module.css";

/** Steps drawn as a bar even when they take no time of their own. */
const SPANNING = new Set(["dr3", "install", "gridoffer"]);

/** A bar the forecast had to place at tomorrow, the soonest anything not done can happen,
 * says so; one placed later by the steps before it needs no such note. */
const tomorrowNote = (at: Scheduled, tomorrow: Date): string => {
  if (at.end?.getTime() === tomorrow.getTime())
    return "This date assumes it clears tomorrow at the earliest.";
  if (at.start?.getTime() === tomorrow.getTime())
    return "This date assumes it starts tomorrow at the earliest.";
  return "";
};
const NAME_WIDTH = 230;
/** The narrowest the chart is drawn; past the panel's width it scrolls. */
const MIN_PX_PER_DAY = 1.15;
/** The buildings the tabs hold, earliest committed COD first (the API's order). Step 2 links
 * only these. */
export const TOP = 5;

const BAR_CLASS: Record<string, string> = {
  done: styles.gDone,
  open: styles.gOpen,
  blocked: styles.gBlocked,
  planned: styles.gPlanned,
};
/** Each bar's state in words, as the legend names it. */
const BAR_LABEL: Record<string, string> = {
  done: "Done",
  open: "In progress",
  blocked: "Blocked",
  planned: "Planned, or waiting on an earlier step",
};
const OWNER_MARK = {
  inrange: styles.markInrange,
  amazon: styles.markAmazon,
  third: styles.markThird,
  both: styles.markBoth,
};
/** A card's state label, by how the card reads (blockers.cardState). */
const STATE_CLASS = {
  blocked: styles.stateBlocked,
  late: styles.stateBlocked,
  open: styles.stateOpen,
  not_tracked: styles.stateNotYet,
  waiting: styles.stateNotYet,
};

/** The signed COD beside the page's projected one, and how far apart they are. */
const codVersus = (signed: Date | null, projected: Date | null): string => {
  if (!projected) return `Signed COD ${signed ? formatDate(signed) : "not on record"}.`;
  if (!signed) return `Signed COD not on record; projected COD ${formatDate(projected)}.`;
  const late = daysBetween(signed, projected);
  const gap =
    late > 0 ? `${late} days after it` : late < 0 ? `${-late} days before it` : "the same day";
  return `Signed COD ${formatDate(signed)}; projected COD ${formatDate(projected)}, ${gap}.`;
};

const Gantt = ({
  b,
  plan,
  projected,
  programme,
  atRisk,
}: {
  b: AmazonBuilding;
  plan: Record<string, Scheduled>;
  /** The projected COD (``projectCod``), which the specialists' date can push back. */
  projected: Date | null;
  programme: PathStep[];
  /** Why a step is at risk (``riskCauses``). */
  atRisk: (id: string) => string[];
}) => {
  const edit = useEdit();
  const now = today();
  const tomorrow = soonest(now);
  const scroll = useRef<HTMLDivElement>(null);
  const [available, setAvailable] = useState(0);
  useLayoutEffect(() => {
    const el = scroll.current;
    if (!el) return;
    const measure = () => setAvailable(el.clientWidth - NAME_WIDTH);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  const cod = b.committed_cod ? day(b.committed_cod) : null;
  const starts = programme.map((s) => plan[s.id].start).filter(
    (d): d is Date => d !== null,
  );
  const from = addDays(
    new Date(Math.min(addDays(now, -90).getTime(), ...starts.map((d) => d.getTime()))),
    -14,
  );
  const ends = [plan.cod.end, projected, cod, now].filter((d): d is Date => d !== null);
  const to = addDays(new Date(Math.max(...ends.map((d) => d.getTime()))), 90);
  const span = daysBetween(from, to);
  const pxPerDay = Math.max(MIN_PX_PER_DAY, available / span);
  const width = Math.floor(span * pxPerDay);
  const x = (d: Date) => Math.round(daysBetween(from, d) * pxPerDay);
  const ticks: Date[] = [];
  for (
    let t = new Date(Date.UTC(from.getUTCFullYear(), from.getUTCMonth() + 1, 1));
    t <= to;
    t = new Date(Date.UTC(t.getUTCFullYear(), t.getUTCMonth() + 1, 1))
  ) {
    ticks.push(t);
  }
  // MSPPA s5.8: the Limited Activities Period (1 to 14 Oct) runs into the Restricted
  // Period (15 Oct to 15 Jan); neither allows an electrical interruption.
  const restricted: [Date, Date][] = [];
  for (let y = from.getUTCFullYear() - 1; y <= to.getUTCFullYear(); y++) {
    const a = new Date(Date.UTC(y, 9, 1));
    const z = new Date(Date.UTC(y + 1, 0, 15));
    if (z > from && a < to) restricted.push([a < from ? from : a, z]);
  }
  // A dashed line's label sits before it: the projected COD is the chart's last date.
  const line = (at: Date, colour: string, label: string, top: boolean, dashed = false) => (
    <Fragment key={label}>
      <div
        className={styles.gLine}
        style={{ left: x(at), borderLeft: `2px ${dashed ? "dashed" : "solid"} ${colour}` }}
      />
      <div
        className={styles.gLineLabel}
        style={{
          left: dashed ? x(at) - 3 : x(at) + 3,
          transform: dashed ? "translateX(-100%)" : undefined,
          color: colour,
          ...(top ? { top: 2 } : { bottom: 2 }),
        }}
      >
        {label}
      </div>
    </Fragment>
  );
  const cols = { gridTemplateColumns: `${NAME_WIDTH}px ${width}px` };
  return (
    <div className={styles.gScroll} ref={scroll}>
      <div className={styles.gantt} style={{ width: NAME_WIDTH + width }}>
        <div className={styles.gHeadRow} style={cols}>
          <div className={styles.gName}>
            <span className={styles.gHead}>Step</span>
          </div>
          <div className={styles.gTrack}>
            {/* The shading below lets the bars take the hover, so its card opens here. */}
            {restricted.map(([a, z]) => (
              <HoverCard
                key={a.toISOString()}
                className={styles.gRestrictedHead}
                style={{ left: x(a), width: x(z) - x(a) }}
                label="Limited Activities and Restricted Periods"
              >
                <div className={styles.cardTitle}>Limited Activities and Restricted Periods</div>
                <p>
                  Limited Activities Period (1 to 14 Oct), then Restricted Period (15 Oct to 15
                  Jan): no electrical interruptions.
                </p>
                <p className={styles.cardRef}>MSPPA s5.8</p>
              </HoverCard>
            ))}
            {ticks.map((t) => (
              <div key={t.toISOString()} className={styles.gTick} style={{ left: x(t) }}>
                {t.toLocaleDateString("en-GB", {
                  month: "short",
                  year: t.getUTCMonth() === 0 ? "2-digit" : undefined,
                  timeZone: "UTC",
                })}
              </div>
            ))}
          </div>
        </div>
        {LANES.map((lane) => (
          <Fragment key={lane.id}>
            <div className={styles.gLane}>{lane.name}</div>
            {programme.filter((s) => s.lane === lane.id).map((s) => {
              // COD sits on the projected COD, which the specialists' later date can set.
              const theirs = s.id === "cod" && projected && plan.cod.end && projected > plan.cod.end;
              const at = theirs ? { ...plan.cod, start: projected, end: projected } : plan[s.id];
              const na = b.steps[s.id].state === "not_applicable";
              const kind = at.waiting && at.kind !== "done" ? "planned" : at.kind;
              const name = (
                <div className={styles.gName}>
                  <span className={OWNER_MARK[s.owner]} />
                  {s.id === "roof" ? (
                    <RoofProcessTitle title={s.title} className={styles.gTitle} />
                  ) : (
                    <span className={styles.gTitle}>
                      {s.title}
                      {s.unapproved && <span className={styles.star}>*</span>}
                    </span>
                  )}
                  {at.kind !== "done" && atRisk(s.id).length > 0 && (
                    <span
                      className={
                        s.id === "roof" ? `${styles.gRisk} ${styles.gRiskLow}` : styles.gRisk
                      }
                    >
                      At risk
                    </span>
                  )}
                </div>
              );
              if (na || !at.start || !at.end) {
                return (
                  <div key={s.id} className={styles.gRow} style={cols}>
                    {name}
                    <div className={styles.gTrack}>
                      <span className={styles.gNote}>
                        {na
                          ? "Not needed"
                          : `Done, date not recorded${b.steps[s.id].note ? `: ${b.steps[s.id].note}` : ""}`}
                      </span>
                    </div>
                  </div>
                );
              }
              const milestone =
                (!s.days && !SPANNING.has(s.id)) || at.start.getTime() === at.end.getTime();
              const left = x(at.start);
              const w = Math.max(4, x(at.end) - left);
              const when = milestone
                ? formatDate(at.end)
                : `${formatDate(at.start)} to ${formatDate(at.end)}`;
              const mark = (
                <HoverCard
                  className={
                    milestone && !s.reviews
                      ? `${styles.gMilestone} ${BAR_CLASS[kind]}`
                      : `${styles.gBar} ${BAR_CLASS[kind]}`
                  }
                  style={milestone && !s.reviews ? { left: x(at.end) } : { left, width: w }}
                  label={`${s.title} · ${when}`}
                  onClick={
                    edit.editing
                      ? () => edit.select({ kind: "step", projectId: b.project_id, stepId: s.id })
                      : undefined
                  }
                >
                  <div className={styles.cardTitle}>
                    {s.title} <PartyChip owner={s.owner} label={s.ownerLabel} />
                  </div>
                  <p>
                    {when} · {BAR_LABEL[kind]}
                    {at.noDate ? " · no date from the DNO" : ""}
                  </p>
                  {s.id === "cod" && <p>{codVersus(cod, theirs ? projected : at.end)}</p>}
                  {!milestone && at.kind !== "done" && lengthWhy(b, s.id) && (
                    <p>{lengthWhy(b, s.id)}</p>
                  )}
                  {at.kind !== "done" && b.steps[s.id].note && (
                    <p>{capitalised(b.steps[s.id].note ?? "")}</p>
                  )}
                  {s.reviews && (
                    <p>
                      Three reviews in parallel:{" "}
                      {b.freehold
                        ? "Amazon technical · Amazon Real Estate approval · Amazon insurance"
                        : DR3_PARTS.map((p) => p.label).join(" · ")}
                    </p>
                  )}
                  {at.kind !== "done" && atRisk(s.id).length > 0 && (
                    <>
                      <div className={styles.riskHead}>At risk</div>
                      <RiskList causes={atRisk(s.id)} />
                    </>
                  )}
                  {s.id === "design" && at.kind !== "done" && designInputs(b).missing.length > 0 ? (
                    <p className={styles.milestoneNote}>
                      This date assumes Amazon&apos;s {designInputs(b).missing.length} outstanding
                      design inputs arrive tomorrow.
                    </p>
                  ) : (
                    at.kind !== "done" &&
                    tomorrowNote(at, tomorrow) && (
                      <p className={styles.milestoneNote}>{tomorrowNote(at, tomorrow)}</p>
                    )
                  )}
                  {at.winter && <p>{at.winter}</p>}
                </HoverCard>
              );
              return (
                <div key={s.id} className={styles.gRow} style={cols}>
                  {name}
                  <div className={styles.gTrack}>
                    {mark}
                    <span
                      className={styles.gDate}
                      style={{ left: milestone ? x(at.end) + 10 : left + w + 5 }}
                    >
                      <Editable
                        target={{ kind: "step", projectId: b.project_id, stepId: s.id }}
                        keys={stepFactKeys(s.id)}
                      >
                        {formatShort(at.end)}
                        {kind === "blocked" ? " · blocked" : ""}
                        {at.noDate ? " · no DNO date" : ""}
                        {theirs ? " · specialists' date" : ""}
                      </Editable>
                    </span>
                  </div>
                </div>
              );
            })}
          </Fragment>
        ))}
        <div className={styles.gOver} style={{ left: NAME_WIDTH, width }}>
          {restricted.map(([a, z]) => (
            <div
              key={a.toISOString()}
              className={styles.gRestricted}
              style={{ left: x(a), width: x(z) - x(a) }}
            />
          ))}
          {line(now, "var(--ao-inrange)", "Today", true)}
          {cod && line(cod, "var(--ao-fg)", "Committed COD", true)}
          {cod &&
            projected &&
            daysBetween(cod, projected) > 0 &&
            line(
              projected,
              slipTone(daysBetween(cod, projected)) === "red" ? "var(--ao-red)" : "var(--ao-amber)",
              "Projected COD",
              false,
              true,
            )}
        </div>
      </div>
    </div>
  );
};

/** Why a blocker is needed by its date: the chain from the committed COD back to it, each
 * step with how long it takes and the window it must fit in. */
const NeededByChain = ({
  b,
  id,
  links,
  programme,
  buffer,
}: {
  b: AmazonBuilding;
  id: string;
  links: Links;
  programme: PathStep[];
  buffer: number;
}) => {
  const chain = neededByChain(b, id, links, buffer);
  if (!chain) return null;
  const title = (sid: string) =>
    programme.find((s) => s.id === sid)?.title ?? stepById(sid).title;
  const install = installLength(b);
  const takes = (sid: string, days: number) =>
    sid === "dr3"
      ? `104 Business Days (${Math.round(days)} days here)`
      : sid === "design"
        ? `${Math.round(days)} days (${designLength(b).basis})`
        : sid === "install"
        ? install
          ? `${Math.round(days)} days (${install.basis})`
          : "no achievable size recorded"
        : days
          ? `${Math.round(days)} days`
          : "-";
  const last = chain[chain.length - 1];
  return (
    <table className={styles.chain}>
      <thead>
        <tr>
          <th>Step</th>
          <th>Takes</th>
          <th>From</th>
          <th>To</th>
        </tr>
      </thead>
      <tbody>
        {chain[0].contract && b.committed_cod && (
          <tr>
            <td>Signed COD</td>
            <td>The {chain[0].contract} below sets the dates</td>
            <td />
            <td>{formatNumeric(day(b.committed_cod))}</td>
          </tr>
        )}
        {chain.slice(0, -1).map((c, i) => (
          <tr key={c.id}>
            <td>{i === 0 ? (c.contract ?? "Signed COD") : title(c.id)}</td>
            <td>
              {i === 0 ? "" : c.toFinish ? "needs the step below by its end" : takes(c.id, c.days ?? 0)}
            </td>
            <td>{i > 0 && c.days && !c.toFinish ? formatNumeric(addDays(c.by, -c.days)) : ""}</td>
            <td>{formatNumeric(c.by)}</td>
          </tr>
        ))}
        {last.floor && (
          <tr>
            <td>
              {last.floor.from === last.id
                ? "We sent it to them"
                : `${title(last.floor.from)} done`}
            </td>
            <td>
              Worked back from the dates above it was needed by{" "}
              {formatNumeric(last.floor.workedBack)}, before we delivered
            </td>
            <td />
            <td>{formatNumeric(last.floor.on)}</td>
          </tr>
        )}
        <tr className={styles.chainEnd}>
          <td>{title(last.id)}</td>
          <td>
            {!last.floor
              ? ""
              : daysBetween(last.floor.on, last.by) === 1
                ? "Expected the day after we delivered"
                : `${takes(last.id, daysBetween(last.floor.on, last.by))} from the day we delivered`}
          </td>
          <td>{last.floor ? formatNumeric(last.floor.on) : ""}</td>
          <td>{formatNumeric(last.by)}</td>
        </tr>
      </tbody>
    </table>
  );
};

export const BuildingView = ({
  buildings,
  steps,
  projectId,
  onSelect,
}: {
  buildings: AmazonBuilding[];
  steps: StoryStep[];
  projectId: string | null;
  onSelect: (projectId: string) => void;
}) => {
  const now = useMemo(today, []);
  const buffer = DEFAULT_BUFFER_DAYS;
  const [why, setWhy] = useState<string | null>(null);
  const [allBlockers, setAllBlockers] = useState(false);
  const edit = useEdit();
  const b = buildings.find((x) => x.project_id === projectId) ?? buildings[0];
  const o = edit.overrides(b.project_id);
  const pid = b.project_id;
  // Comments are InRange's review of an item, for its keeper: none for a viewer outside
  // InRange, whose sign-in the review routes refuse.
  const external = useContext(UserContext).role === "external";
  const comments = useReviewComments(external ? null : { project_id: pid });
  // A building opened by URL outside the five keeps its own chip while it is open.
  const shown = useMemo(() => {
    const top = buildings.slice(0, TOP);
    return top.includes(b) ? top : [...top, b];
  }, [buildings, b]);
  const index = shown.indexOf(b);
  // The programme follows the links Step 1 shows, edits included.
  const links = useMemo(() => programmeLinks(steps), [steps]);
  const onStepOne = useMemo(() => asOnStepOne(steps), [steps]);
  const plans = useMemo(
    () =>
      new Map(buildings.map((x) => [x.project_id, schedule(x, now, links)])),
    [buildings, now, links],
  );
  const slipOf = (x: AmazonBuilding): number | null => {
    const end = projectCod(x, plans.get(x.project_id)?.cod.end ?? null).date;
    return x.committed_cod && end ? daysBetween(day(x.committed_cod), end) : null;
  };

  // The arrow keys move the selection; when focus was on the tabs it follows the selected
  // one, even where the tab it was on goes (a building opened outside the five).
  const tabList = useRef<HTMLDivElement>(null);
  const leftColumn = useRef<HTMLDivElement>(null);
  const cardList = useRef<HTMLDivElement>(null);
  const refocus = useRef(false);
  useEffect(() => {
    const key = (e: KeyboardEvent) => {
      if ((e.target as HTMLElement).closest("input, select, textarea")) return;
      if (e.altKey || e.metaKey || e.ctrlKey || e.shiftKey) return;
      if (e.key !== "ArrowLeft" && e.key !== "ArrowRight") return;
      const step = e.key === "ArrowRight" ? 1 : -1;
      refocus.current = Boolean(tabList.current?.contains(document.activeElement));
      onSelect(shown[(index + step + shown.length) % shown.length].project_id);
    };
    window.addEventListener("keydown", key);
    return () => window.removeEventListener("keydown", key);
  }, [shown, index, onSelect]);

  useEffect(() => {
    setWhy(null);
    setAllBlockers(false);
  }, [b.project_id]);

  useEffect(() => {
    if (!refocus.current) return;
    refocus.current = false;
    tabList.current?.querySelector<HTMLButtonElement>('[aria-selected="true"]')?.focus();
  }, [b.project_id]);

  const plan = plans.get(b.project_id) as Record<string, Scheduled>;
  const programme = forBuilding(b, onStepOne);
  const need = neededBy(b, links, buffer);
  const uncounted = uncountedFor(plan, need);
  const cod = b.committed_cod ? day(b.committed_cod) : null;
  const slip = slipOf(b);
  const projection = projectCod(b, plan.cod.end);
  const cardHidden = (id: string) => isHidden(o, stepKey(id, "hidden"));
  const blockers = blockersFor(b, need, links, programme, steps, now);
  const readerBlockers = blockers.filter((x) => !cardHidden(x.step.id));
  const cards = blockerCards(b, blockers, need, plan, links, programme, steps, now);
  // A hidden card's items go where they would with the card gone, so readers still see them.
  const items = blockerItems(
    b,
    cards
      .filter((x) => !cardHidden(x.step.id))
      .flatMap((x) => [x.step.id, ...(x.parts ?? []).map((p) => p.step.id)]),
  );
  /** A card's steps: the box's own and each part drawn inside it. */
  const cardSteps = (x: Blocker) => [x.step.id, ...(x.parts ?? []).map((p) => p.step.id)];
  const title = (id: string) => programme.find((s) => s.id === id)?.title ?? stepById(id).title;
  const atRisk = (id: string): string[] => riskCauses(b, links, readerBlockers, id, now, need);
  const first = blockers.find((x) => !cardHidden(x.step.id));
  const shownBlockers = cards.filter((x) => edit.editing || !cardHidden(x.step.id));
  // The cards that fit beside the story and the milestones, the most urgent first; the
  // rest fold behind a button.
  const { fit, measuring } = useFitBeside(
    leftColumn,
    cardList,
    shownBlockers.length,
    `${b.project_id}|${edit.editing}|${shownBlockers.map((x) => x.step.id).join(",")}`,
  );
  const folded = fit != null && !measuring && !allBlockers;
  const achievable = achievableSize(b);
  const shortfall = achievable.tone;
  const codTone = slip != null && slip > 0 ? slipTone(slip) : undefined;

  // The verdict in parts, a bold part as [text, class]: rendered as is on the page, and as
  // plain text for the pane, which shows it beside an override.
  const verdictParts: (string | [string, string])[] = !cod
    ? [
        `${b.site_code} has no committed COD on record yet, so the page cannot say whether it is on time.`,
      ]
    : !projection.date
      ? [
          `${b.site_code} has no achievable size or Schedule 1 installation dates on record yet, so the page cannot say how long installation takes or whether it is on time.`,
        ]
      : codTone
        ? [
            `${b.site_code} is projected `,
            [`${slip} days`, codTone === "red" ? styles.late : styles.lateWarn],
            ` after its committed COD of ${formatDate(cod)}, ${
              projection.bySpecialists
                ? "on the specialists' achievable COD."
                : "if every open step clears tomorrow at the earliest."
            }`,
            ...(first
              ? [
                  " The first blocker is ",
                  [first.step.title.toLowerCase(), ""] as [string, string],
                  ` (${first.step.ownerLabel ?? OWNER_NAME[first.step.owner]})` +
                    (first.need ? `, needed by ${formatDate(first.need)}` : "") +
                    ".",
                ]
              : []),
          ]
        : [`${b.site_code} is `, ["on track", styles.ok], ` for its committed COD of ${formatDate(cod)}.`];
  const verdictText = verdictParts.map((x) => (typeof x === "string" ? x : x[0])).join("");
  const verdict =
    wordingOf(o, buildingKey("verdict")) ??
    verdictParts.map((x, i) =>
      typeof x === "string" ? (
        <Fragment key={i}>{x}</Fragment>
      ) : (
        <b key={i} className={x[1] || undefined}>
          {x[0]}
        </b>
      ),
    );
  const captionText = achievable.caption;
  const wordings = [
    { key: buildingKey("verdict"), label: "Verdict sentence", pageText: verdictText },
    {
      key: buildingKey("achievable_caption"),
      label: "Achievable size caption",
      pageText: captionText,
    },
  ];
  const wording = (key: string, label: string, pageText: string): Target => ({
    kind: "wording",
    projectId: pid,
    key,
    label,
    pageText,
  });
  const facts: Target = { kind: "building", projectId: pid };
  const worked = (label: string): Target => ({
    kind: "computed",
    projectId: pid,
    label,
    from: [
      facts,
      { kind: "contract", projectId: pid },
      ...programme.map((x): Target => ({ kind: "step", projectId: pid, stepId: x.id })),
    ],
  });

  return (
    <EditLayout
      pane={<EditPane page="site" buildings={[b]} portfolio={buildings} steps={steps} wordings={wordings} />}
    >
    {/* Text selected on an item needed from Amazon is a comment on it, for the
        specialist keeping it to apply (docs/Y2026-Q4/design-amazon-item-comments.md);
        off while editing, where a click edits the page. */}
    <ReviewProvider
      available={!edit.editing && !external}
      defaultDoc={`project:${pid}`}
      defaultLevel="amazon_item"
      comments={edit.editing ? [] : (comments.data?.comments ?? [])}
      phases={comments.data?.phases}
      projectId={pid}
      onQuote={null}
    >
    <div className={styles.building}>
      <div className={styles.buildingTabs}>
        <div className={styles.buildingTabList} role="tablist" aria-label="Buildings" ref={tabList}>
        {shown.map((x) => {
          const s = slipOf(x);
          const selected = x.project_id === b.project_id;
          return (
            <button
              key={x.project_id}
              type="button"
              role="tab"
              id={`building-tab-${x.project_id}`}
              aria-selected={selected}
              aria-controls="building-panel"
              tabIndex={selected ? 0 : -1}
              className={styles.buildingTab}
              onClick={() => !selected && onSelect(x.project_id)}
            >
              <span className={styles.buildingTabCode}>
                <span
                  className={
                    s == null
                      ? styles.dotNone
                      : s <= 0
                        ? styles.dotOnTime
                        : slipTone(s) === "red"
                          ? styles.dotLate
                          : styles.dotWarn
                  }
                />
                {x.site_code}
              </span>
              <small>
                {x.committed_cod ? `COD ${formatNumeric(day(x.committed_cod))}` : "No COD yet"}
              </small>
            </button>
          );
        })}
        </div>
      </div>

      <div
        className={styles.building}
        role="tabpanel"
        id="building-panel"
        aria-labelledby={`building-tab-${b.project_id}`}
      >
      <p
        className={
          slip == null
            ? styles.verdictUnknown
            : codTone === "red"
              ? styles.verdict
              : codTone === "amber"
                ? styles.verdictWarn
                : styles.verdictOk
        }
      >
        <Editable
          target={wording(buildingKey("verdict"), "Verdict sentence", verdictText)}
          keys={[buildingKey("verdict")]}
        >
          {verdict}
        </Editable>
      </p>
      {uncounted.length > 0 && <p className={styles.uncounted}>{uncountedText(uncounted)}.</p>}

      <div className={styles.factTiles}>
        <div className={styles.fact}>
          <span>PPA committed size</span>
          <b>
            <Editable
              target={facts}
              keys={[buildingKey("committed_kwp"), buildingKey("committed_kw_ac")]}
            >
              {ppaText(b)}
            </Editable>
          </b>
          <small>
            <Editable
              target={{ kind: "step", projectId: pid, stepId: "sppa" }}
              keys={[stepKey("sppa", "state")]}
            >
              {b.steps.sppa?.state === "done"
                ? "From the signed SPPA"
                : b.steps.sppa?.state === "open"
                  ? "From the draft SPPA, not yet signed"
                  : "No SPPA on record"}
            </Editable>
          </small>
        </div>
        <div
          className={
            shortfall === "red"
              ? styles.factAlert
              : shortfall === "amber"
                ? styles.factWarn
                : styles.fact
          }
        >
          <span>Currently achievable size</span>
          <b>
            <Editable
              target={facts}
              keys={[buildingKey("achievable_kwp"), buildingKey("achievable_kw_ac")]}
            >
              {pairParts(achievable.kwp, achievable.kwAc).map((part, i, parts) => (
                <Fragment key={part}>
                  {i > 0 && " "}
                  <span className={styles.sizePart}>
                    {part}
                    {i < parts.length - 1 && " /"}
                  </span>
                </Fragment>
              ))}
              {!achievable.kwp && !achievable.kwAc && "-"}
            </Editable>
          </b>
          <small>
            <Editable
              target={wording(
                buildingKey("achievable_caption"),
                "Achievable size caption",
                captionText,
              )}
              keys={[buildingKey("achievable_caption")]}
            >
              {wordingOf(o, buildingKey("achievable_caption")) ?? captionText}
            </Editable>
          </small>
          {achievable.redesign && <small>Subject to PV redesign</small>}
        </div>
        <div className={styles.fact}>
          <span>Committed COD</span>
          <b>
            <Editable target={facts} keys={[buildingKey("committed_cod")]}>
              {cod ? formatNumeric(cod) : "-"}
            </Editable>
          </b>
          <small>
            {cod ? "SPPA Schedule 1" : "Not on record"}
          </small>
        </div>
        <div
          className={
            codTone === "red" ? styles.factAlert : codTone === "amber" ? styles.factWarn : styles.fact
          }
        >
          <span>Projected COD</span>
          <b>
            <Editable target={worked("Projected COD")} keys={[]}>
              {projection.date ? formatNumeric(projection.date) : "-"}
            </Editable>
          </b>
          <small>
            {!projection.date
              ? "Nothing to time installation by yet"
              : slip == null
                ? "No committed COD to compare"
                : slip > 0
                  ? projection.bySpecialists
                    ? `${slip} days late: the specialists' achievable COD`
                    : `${slip} days late if open steps clear today`
                  : "On time"}
          </small>
        </div>
      </div>

      <div className={styles.two}>
        <div className={styles.stack} ref={leftColumn}>
          <div className={styles.panel}>
            <h3>Where {b.site_code} is in the story</h3>
            <ChapterPanel
              key={b.project_id}
              b={b}
              steps={steps}
              o={o}
              need={need}
              now={now}
              worked={worked}
            />
          </div>
          <Milestones
            b={b}
            plan={plan}
            projection={projection}
            buffer={buffer}
            blockers={readerBlockers}
            links={links}
            now={now}
            worked={worked}
          />
        </div>
        <div className={styles.panel}>
          <h3>Blockers</h3>
          {shownBlockers.length ? (
            <div className={styles.asks} ref={cardList}>
              {(folded ? shownBlockers.slice(0, fit) : shownBlockers).map((x) => {
                const overdue = x.need && x.need < now ? daysBetween(x.need, now) : 0;
                const startsIn = x.start ? daysBetween(now, x.start) : null;
                // Past its needed-by date, whatever its state: the card's red edge.
                const late = x.late || overdue > 0;
                const shownAs = cardState(x);
                const open = why === x.step.id;
                // A point the other side owes is one of the items needed from them.
                const owed = [
                  ...cardSteps(x).flatMap((id) => items.byBlocker.get(id) ?? []),
                  ...pointNeeds(b, cardSteps(x)),
                ];
                const ours = cardSteps(x)
                  .flatMap((id) => b.steps[id]?.open_points ?? [])
                  .filter((p) => !owedOutside(p));
                return (
                  <div
                    key={x.step.id}
                    className={`${shownAs === "blocked" || shownAs === "late" ? styles.askBlocked : styles.ask}${late ? ` ${styles.askLate}` : ""}${cardHidden(x.step.id) ? ` ${styles.askHidden}` : ""}`}
                  >
                    <div className={styles.askHead}>
                      <span className={styles.askTitle}>
                        <Editable target={{ kind: "stepOne", stepId: x.step.id }} keys={[]}>
                          {x.step.title}
                        </Editable>{" "}
                        <PartyChip owner={x.step.owner} label={x.step.ownerLabel} />{" "}
                        <Editable
                          target={{ kind: "step", projectId: pid, stepId: x.step.id }}
                          keys={[stepKey(x.step.id, "state")]}
                        >
                          {x.boxDone ? (
                            <span className={styles.stateDone}>
                              {x.step.owner === "inrange" ? "Handed off by InRange" : "Done"}
                            </span>
                          ) : (
                            <span className={STATE_CLASS[shownAs]}>{STATE_LABEL[shownAs]}</span>
                          )}
                        </Editable>
                        {cardHidden(x.step.id) && (
                          <span className={styles.hiddenTag}> Hidden from readers</span>
                        )}
                      </span>
                      {((x.start && startsIn != null) || x.step.id === "roof") && (
                        <span className={styles.askSide}>
                          {x.start && startsIn != null && (
                            <span className={styles.askDue}>
                              Planned start{" "}
                              <Editable target={worked(`Planned start for ${x.step.title}`)} keys={[]}>
                                <b>{startsIn === 1 ? "tomorrow" : formatDate(x.start)}</b>
                              </Editable>
                              {startsIn > 1 ? ` · in ${startsIn} days` : ""}
                              {x.need && overdue > 0 && (
                                <span className={styles.late}>
                                  {" "}
                                  · needed by {formatDate(x.need)}, {overdue} days ago
                                </span>
                              )}
                            </span>
                          )}
                          {x.step.id === "roof" && <RoofProcessLink />}
                        </span>
                      )}
                    </div>
                    {(() => {
                      const line = wordingOf(o, cardKey(x.step.id)) ?? x.why;
                      return edit.editing || line ? (
                        <Editable
                          target={{ kind: "step", projectId: pid, stepId: x.step.id }}
                          keys={cardKeys(x.step.id)}
                        >
                          <span className={styles.askWhy}>{line || <i>No line</i>}</span>
                        </Editable>
                      ) : null;
                    })()}
                    {x.parts?.map((p) => (
                      <span key={p.step.id} className={styles.askPart}>
                        <b>{p.step.title}</b>{" "}
                        <span className={STATE_CLASS[cardState(p)]}>{STATE_LABEL[cardState(p)]}</span>
                        {p.why && ` · ${p.why}`}
                      </span>
                    ))}
                    {x.holds.length > 0 && (
                      <span className={styles.askHolds}>
                        <Editable target={{ kind: "stepOne", stepId: x.step.id }} keys={[]}>
                          Holds up: {x.holds.join(", ")}
                        </Editable>
                      </span>
                    )}
                    {ours.length > 0 && <OpenPoints points={ours} />}
                    {owed.length > 0 && (
                      <NeededFromAmazon
                        b={b}
                        items={owed}
                        stepId={x.step.id}
                        steps={steps}
                        need={need}
                        now={now}
                      />
                    )}
                    {x.start && (
                      <div className={styles.askActs}>
                        <button
                          type="button"
                          className={styles.askToggle}
                          aria-expanded={open}
                          onClick={() => setWhy(open ? null : x.step.id)}
                        >
                          Why this date <Chevron open={open} />
                        </button>
                      </div>
                    )}
                    {open && (
                      <div className={styles.askDetail}>
                        <span className={styles.askWhy}>
                          {plannedStartWhy(plan, links, x.startOf ?? x.step.id, now, title)}
                        </span>
                        {x.need && (
                          <>
                            <span className={styles.askWhy}>
                              Needed by {formatDate(x.need)} to hold the dates, worked back below.
                            </span>
                            <NeededByChain
                              b={b}
                              id={x.needOf ?? x.step.id}
                              links={links}
                              programme={programme}
                              buffer={buffer}
                            />
                          </>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
              {fit != null && !measuring && (
                <button
                  type="button"
                  className={styles.askToggle}
                  aria-expanded={allBlockers}
                  onClick={() => setAllBlockers(!allBlockers)}
                >
                  {allBlockers
                    ? "Show fewer"
                    : `Show ${shownBlockers.length - fit} more, blocking next`}{" "}
                  <Chevron open={allBlockers} />
                </button>
              )}
            </div>
          ) : (
            <p className={styles.sub}>Nothing is blocked or waiting right now.</p>
          )}
          {/* Only an item for a card hidden from readers, or for a step Step 1 left out:
              every other step with an item has its own card. */}
          {items.elsewhere.length > 0 && (
            <NeededFromAmazon
              b={b}
              items={items.elsewhere}
              stepId={null}
              steps={steps}
              need={need}
              now={now}
              title="Also needed, for steps with no card above"
            />
          )}
        </div>
      </div>

      <div className={styles.panel}>
        <h3>
          Programme <small>One row per step, in the four lanes of Step 1. Hover a bar for its dates.</small>
        </h3>
        <div className={styles.legend}>
          <span>
            <i className={styles.gDone} /> Done
          </span>
          <span>
            <i className={styles.gOpen} /> In progress
          </span>
          <span>
            <i className={styles.gBlocked} /> Blocked
          </span>
          <span>
            <i className={styles.gPlanned} /> Planned, or waiting on an earlier step
          </span>
          <span>
            <i className={styles.gRestrictedSwatch} /> Limited Activities and Restricted
            Periods, 1 Oct to 15 Jan
          </span>
        </div>
        <Gantt
          b={b}
          plan={plan}
          projected={projection.date}
          programme={programme}
          atRisk={atRisk}
        />
      </div>
      </div>
    </div>
    </ReviewProvider>
    </EditLayout>
  );
};

